import React from 'react';
import { IonButton } from '@ionic/react';
import { ArrowLeft } from 'lucide-react';
import { cn } from '../../lib/utils';

// Primitives ported from the design prototype. Button is a real IonButton
// (native ripple + the --ion-color-* system from the theme bridge); it renders
// correctly because it now lives inside IonContent (see the CitizenApp shell).

type ButtonVariant = 'default' | 'primary' | 'secondary' | 'outline' | 'ghost' | 'dark';
type ButtonSize = 'default' | 'sm' | 'lg' | 'icon' | 'iconlg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

// Prototype variants → IonButton fill/color (+ CSS-var overrides for the two
// custom looks that don't map to a stock Ion color).
const BTN_VARIANT: Record<ButtonVariant, { fill: 'solid' | 'outline' | 'clear'; color?: string; vars?: React.CSSProperties }> = {
  default: { fill: 'solid', color: 'primary' },
  primary: { fill: 'solid', color: 'primary' },
  secondary: { fill: 'solid', color: 'secondary', vars: { '--border-width': '1px', '--border-color': 'var(--border)' } as React.CSSProperties },
  outline: { fill: 'outline', vars: { '--border-color': 'var(--border)', '--color': 'var(--foreground)' } as React.CSSProperties },
  ghost: { fill: 'clear', vars: { '--color': 'var(--foreground)' } as React.CSSProperties },
  dark: { fill: 'solid', color: 'tertiary' },
};

// Drive host height + radius/padding to the prototype metrics. IonButton's
// native part fills the host height by default, so setting `height` sizes the
// whole control (no ::part overrides needed — that's what broke the prior try).
const BTN_SIZE: Record<ButtonSize, React.CSSProperties> = {
  default: { height: '2.25rem', fontSize: '0.875rem', '--border-radius': 'var(--radius-md)', '--padding-start': '1rem', '--padding-end': '1rem' } as React.CSSProperties,
  sm: { height: '2rem', fontSize: '0.75rem', '--border-radius': 'var(--radius-md)', '--padding-start': '0.75rem', '--padding-end': '0.75rem' } as React.CSSProperties,
  lg: { height: '3rem', fontSize: '1rem', '--border-radius': 'var(--radius-lg)', '--padding-start': '1.5rem', '--padding-end': '1.5rem' } as React.CSSProperties,
  icon: { height: '2.25rem', width: '2.25rem', '--border-radius': 'var(--radius-md)', '--padding-start': '0', '--padding-end': '0' } as React.CSSProperties,
  iconlg: { height: '2.75rem', width: '2.75rem', '--border-radius': '9999px', '--padding-start': '0', '--padding-end': '0' } as React.CSSProperties,
};

export function Button({ children, variant = 'default', size = 'default', className = '', style, ...p }: ButtonProps) {
  const v = BTN_VARIANT[variant];
  // Screens pass `w-full` to mean a full-width button → IonButton needs expand="block".
  const expand = /\bw-full\b/.test(className) ? 'block' : undefined;
  return (
    <IonButton
      mode="ios"
      fill={v.fill}
      color={v.color}
      expand={expand}
      className={cn('cc-btn', className)}
      style={{ margin: 0, '--box-shadow': 'none', ...v.vars, ...BTN_SIZE[size], ...(style as React.CSSProperties) } as React.CSSProperties}
      {...(p as Record<string, unknown>)}
    >
      {children}
    </IonButton>
  );
}

export function Card({
  className = '',
  children,
  style,
  ...p
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('rounded-xl border bg-[var(--card)] text-[var(--card-foreground)] card-soft', className)}
      style={{ borderColor: 'var(--border)', ...(style || {}) }}
      {...p}
    >
      {children}
    </div>
  );
}

type BadgeVariant = 'default' | 'secondary' | 'outline' | 'accent' | 'gold' | 'sage';

export function Badge({
  variant = 'secondary',
  className = '',
  children,
}: {
  variant?: BadgeVariant;
  className?: string;
  children: React.ReactNode;
}) {
  const v: Record<BadgeVariant, string> = {
    default: 'bg-[var(--primary)] text-white border-transparent',
    secondary: 'bg-[var(--secondary)] text-[color:var(--secondary-foreground)] border-transparent',
    outline: 'text-[var(--foreground)] border border-[var(--border)] bg-transparent',
    accent: 'bg-[var(--accent)] text-[color:var(--accent-foreground)] border-transparent',
    gold: 'bg-[var(--gold-soft)] text-[var(--accent-foreground)] border-transparent',
    sage: 'bg-[#DDEDD9] text-[#2D5A2D] border-transparent',
  };
  return (
    <span
      className={cn(
        'whitespace-nowrap inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold border',
        v[variant],
        className
      )}
    >
      {children}
    </span>
  );
}

type AvatarTone = 'accent' | 'primary' | 'dark' | 'muted';

export function Avatar({
  initials,
  size = 40,
  tone = 'accent',
  src,
  alt,
}: {
  initials: string;
  size?: number;
  tone?: AvatarTone;
  src?: string;
  alt?: string;
}) {
  const tones: Record<AvatarTone, { bg: string; fg: string }> = {
    accent: { bg: 'var(--accent)', fg: 'var(--primary)' },
    primary: { bg: 'var(--primary)', fg: 'white' },
    dark: { bg: 'var(--hero-deep)', fg: 'var(--hero-light-accent)' },
    muted: { bg: 'var(--muted)', fg: 'var(--muted-foreground)' },
  };
  const t = tones[tone];
  const base: React.CSSProperties = {
    width: size,
    height: size,
    background: t.bg,
    color: t.fg,
    fontSize: size * 0.36,
    letterSpacing: '-0.02em',
  };
  if (src) {
    return (
      <div
        className="rounded-full overflow-hidden shrink-0 flex items-center justify-center"
        style={{ ...base, padding: 0 }}
      >
        <img
          src={src}
          alt={alt || initials}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      </div>
    );
  }
  return (
    <div className="rounded-full flex items-center justify-center font-bold shrink-0" style={base}>
      {initials}
    </div>
  );
}

export function PageHeader({
  title,
  onBack,
  right,
  transparent,
}: {
  title: React.ReactNode;
  onBack?: () => void;
  right?: React.ReactNode;
  transparent?: boolean;
}) {
  return (
    <div
      className={cn('app-header flex items-center justify-between px-4 sticky top-0 z-30', transparent ? '' : 'backdrop-blur')}
      style={transparent ? {} : { background: 'color-mix(in oklab, var(--background) 92%, transparent)' }}
    >
      {onBack ? (
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="w-6 h-6" />
        </Button>
      ) : (
        <div className="w-9 h-9" />
      )}
      <h1 className="text-lg font-semibold tracking-tight">{title}</h1>
      <div className="flex items-center gap-1">{right}</div>
    </div>
  );
}

export function SectionLabel({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between mb-3 px-1">
      <h3 className="font-bold text-[11px] uppercase tracking-widest text-[color:var(--muted-foreground)]">
        {children}
      </h3>
      {right}
    </div>
  );
}

// Circular countdown ring (election countdowns)
export function CountdownRing({
  days,
  total = 365,
  size = 100,
  stroke = 8,
  label = 'days',
}: {
  days: number;
  total?: number;
  size?: number;
  stroke?: number;
  label?: string;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, 1 - days / total));
  const offset = c * (1 - pct);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--hero-light-accent)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 700ms ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
        <span className="text-3xl font-extrabold leading-none tabular-nums">{days}</span>
        <span className="text-[10px] uppercase tracking-widest opacity-80 mt-0.5">{label}</span>
      </div>
    </div>
  );
}

// Sparkline for priority pulse
export function Sparkline({
  data,
  w = 88,
  h = 22,
  color = 'var(--primary)',
  fill = true,
}: {
  data: number[];
  w?: number;
  h?: number;
  color?: string;
  fill?: boolean;
}) {
  if (!data || !data.length) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const span = Math.max(1, max - min);
  const step = w / (data.length - 1);
  const points = data.map((v, i) => [i * step, h - ((v - min) / span) * (h - 2) - 1] as const);
  const path = points.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const area = path + ` L ${w} ${h} L 0 ${h} Z`;
  const last = points[points.length - 1]!;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      {fill && <path d={area} fill={color} opacity="0.12" />}
      <path d={path} stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="2" fill={color} />
    </svg>
  );
}

// Brand social glyphs (filled paths, not in lucide)
const SOCIAL_GLYPHS: Record<string, React.ReactNode> = {
  x: (
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  ),
  instagram: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
    </>
  ),
  facebook: (
    <path d="M13 22v-8h3l.5-4H13V7.5c0-1.2.3-2 2-2h2V2.1C16.7 2 15.5 2 14.3 2 11.6 2 10 3.7 10 6.8V10H7v4h3v8z" />
  ),
  linkedin: (
    <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9h4v12H3zM10 9h3.8v1.7h.1c.5-.9 1.8-1.9 3.7-1.9 4 0 4.7 2.6 4.7 6V21h-4v-5.4c0-1.3 0-3-1.8-3s-2.1 1.4-2.1 2.9V21h-4z" />
  ),
  tiktok: <path d="M19 8.5a6 6 0 0 1-3.5-1.1V15a5 5 0 1 1-5-5v3a2 2 0 1 0 2 2V2h3a4 4 0 0 0 4 4z" />,
};

export function SocialGlyph({ kind, className = 'w-4 h-4' }: { kind: string; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      {SOCIAL_GLYPHS[kind]}
    </svg>
  );
}
